/**
 * @fileoverview "Connect Claude" settings page — issue, view, and revoke MCP tokens.
 * @module features/mcp/pages/McpConnectPage
 */

import { useEffect, useMemo, useState, type ReactElement } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { ArrowLeft, Plug } from 'lucide-react'
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/shared/components/ui'
import { ScrollReveal } from '@/shared/components/ui'
import { Kbd } from '@/components/ui/kbd'
import {
  useIssueMcpToken,
  useMcpTokens,
  useRevokeMcpToken,
} from '../hooks/useMcpTokens'
import type { IssuedMcpToken } from '../schemas/mcpToken'
import { GenerateTokenDialog } from '../components/GenerateTokenDialog'
import { IssuedTokenDialog } from '../components/IssuedTokenDialog'
import { McpTokenList } from '../components/McpTokenList'
import { buildClaudeDesktopConfig } from '../utils/buildClientConfig'
import { computeMcpUrl } from '../utils/computeMcpUrl'

/**
 * Settings page for managing MCP personal access tokens.
 *
 * Lets the user copy the MCP endpoint URL, generate new tokens (shown once),
 * and revoke existing ones.
 */
export function McpConnectPage(): ReactElement {
  const navigate = useNavigate()
  const tokensQuery = useMcpTokens()
  const issueMutation = useIssueMcpToken()
  const revokeMutation = useRevokeMcpToken()

  const [generateOpen, setGenerateOpen] = useState(false)
  const [issuedToken, setIssuedToken] = useState<IssuedMcpToken | null>(null)

  const configSnippet = useMemo(() => buildClaudeDesktopConfig(computeMcpUrl(), null), [])

  // Esc to go back, but not when a token-management dialog is open.
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape' && !generateOpen && issuedToken == null) {
        event.preventDefault()
        navigate(-1)
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [generateOpen, issuedToken, navigate])

  const handleCopyConfig = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(configSnippet)
      toast.success('Config copied to clipboard')
    } catch {
      toast.error('Could not copy config. Select and copy manually.')
    }
  }

  const handleGenerate = (name: string | null): void => {
    issueMutation.mutate(
      { name },
      {
        onSuccess: (token) => {
          setGenerateOpen(false)
          setIssuedToken(token)
        },
        onError: (error) => {
          toast.error('Failed to generate token', {
            description: error.message,
          })
        },
      },
    )
  }

  const handleRevoke = (id: string): void => {
    revokeMutation.mutate(id, {
      onSuccess: () => {
        toast.success('Token revoked')
      },
      onError: (error) => {
        toast.error('Failed to revoke token', {
          description: error.message,
        })
      },
    })
  }

  return (
    <div className="min-h-screen pt-18">
      <div className="max-w-screen-xl mx-auto px-4 lg:px-8 py-8 space-y-8">
        <ScrollReveal blur>
          <div className="flex items-center gap-4 mb-8">
            <Button variant="ghost" onClick={() => navigate(-1)} className="gap-2">
              <ArrowLeft className="h-5 w-5" />
              <Kbd>Esc</Kbd>
            </Button>
          </div>
        </ScrollReveal>

        <ScrollReveal blur>
          <Card className="glass-card">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2">
                <Plug className="size-5" />
                Connect Claude
              </CardTitle>
              <CardDescription>
                Generate personal access tokens to let Claude (or any MCP client) read
                your analytics over the Model Context Protocol.
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-4 space-y-6">
              <div className="space-y-2">
                <p className="text-sm font-medium">MCP client config</p>
                <pre
                  data-testid="mcp-config-snippet"
                  className="max-h-72 overflow-auto rounded-md bg-muted px-3 py-2 font-mono text-xs"
                >
                  {configSnippet}
                </pre>
                <div className="flex flex-col sm:flex-row gap-2">
                  <Button variant="secondary" onClick={() => void handleCopyConfig()}>
                    Copy config
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Paste this snippet under <code className="font-mono">mcpServers</code> in your MCP
                  client config (Claude Code <code className="font-mono">.mcp.json</code>, Claude
                  Desktop <code className="font-mono">claude_desktop_config.json</code>, Cursor,
                  …) and replace <code className="font-mono">&lt;paste-token-here&gt;</code> with a
                  token generated below. Restart the client to apply.
                </p>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">Your tokens</p>
                  <p className="text-xs text-muted-foreground">
                    Tokens are issued per user and can be revoked at any time.
                  </p>
                </div>
                <Button onClick={() => setGenerateOpen(true)}>Generate token</Button>
              </div>

              <McpTokenList
                tokens={tokensQuery.data ?? []}
                isLoading={tokensQuery.isLoading}
                onRevoke={handleRevoke}
                revokingId={revokeMutation.isPending ? (revokeMutation.variables ?? null) : null}
              />

              {tokensQuery.isError ? (
                <p className="text-sm text-destructive">
                  Could not load tokens: {tokensQuery.error.message}
                </p>
              ) : null}
            </CardContent>
          </Card>
        </ScrollReveal>
      </div>

      <GenerateTokenDialog
        open={generateOpen}
        onOpenChange={setGenerateOpen}
        onSubmit={handleGenerate}
        isPending={issueMutation.isPending}
      />

      <IssuedTokenDialog token={issuedToken} onClose={() => setIssuedToken(null)} />
    </div>
  )
}
